/* Smooth-film teaching model. Fresnel equations: Byrnes, arXiv:1603.02720.
 * Ti n,k: Johnson & Christy (1974), CC0 data from RefractiveIndex.INFO.
 * CIE 1931 tabulated observer data: Colour Science (BSD 3-clause), see below.
 * Constant oxide index is an illustrative assumption, not a measured recipe. */
((root, factory) => {
  const model = factory();
  if (typeof module === 'object' && module.exports) module.exports = model;
  else root.ZMWThinFilm = model;
})(typeof window === 'undefined' ? globalThis : window, () => {
  'use strict';
  const OXIDE_INDEX = 2.4;
  const LIGHT_SPEED = 299.792458; // nm per femtosecond
  const TI = [[368,1.9,2.9],[381,1.99,2.93],[397,2.08,2.95],[413,2.14,2.98],[431,2.21,3.01],[451,2.27,3.04],[471,2.32,3.1],[496,2.36,3.19],[521,2.44,3.3],[549,2.54,3.43],[582,2.6,3.58],[617,2.67,3.72],[659,2.76,3.84],[704,2.86,3.96],[756,3,4.01],[821,3.21,4.01]];
  const complex = (re, im = 0) => [re, im];
  const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
  const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
  const mul = (a, b) => [a[0]*b[0] - a[1]*b[1], a[0]*b[1] + a[1]*b[0]];
  const div = (a, b) => {
    const norm = b[0]*b[0] + b[1]*b[1];
    return [(a[0]*b[0] + a[1]*b[1])/norm, (a[1]*b[0] - a[0]*b[1])/norm];
  };
  const squareRoot = a => {
    const length = Math.hypot(...a);
    return [Math.sqrt(Math.max(0, (length+a[0])/2)), Math.sign(a[1] || 1)*Math.sqrt(Math.max(0,(length-a[0])/2))];
  };
  const magnitudeSquared = a => a[0]*a[0] + a[1]*a[1];
  const phase = a => Math.atan2(a[1], a[0]);
  const phasor = radians => [Math.cos(radians), Math.sin(radians)];
  const titaniumIndex = wavelength => {
    for (let i=1; i<TI.length; i++) {
      if (wavelength <= TI[i][0]) {
        const a=TI[i-1], b=TI[i], t=(wavelength-a[0])/(b[0]-a[0]);
        return [a[1]+t*(b[1]-a[1]), a[2]+t*(b[2]-a[2])];
      }
    }
    return TI[TI.length-1].slice(1);
  };
  const sample = (thickness, angle, wavelength) => {
    const theta = angle*Math.PI/180;
    const sin0 = Math.sin(theta), cos0 = Math.cos(theta);
    const sin1 = sin0/OXIDE_INDEX, cos1 = Math.sqrt(1-sin1*sin1);
    const metal = titaniumIndex(wavelength);
    // q = n cos(theta), with the passive-metal square-root branch.
    const q0=complex(cos0), q1=complex(OXIDE_INDEX*cos1);
    const q2=squareRoot(sub(mul(metal,metal),complex(sin0*sin0)));
    const r01=div(sub(q0,q1),add(q0,q1));
    const r12=div(sub(q1,q2),add(q1,q2));
    const roundTrip=4*Math.PI*OXIDE_INDEX*thickness*cos1/wavelength;
    const returning=mul(r12,phasor(roundTrip));
    const denominator=add(complex(1),mul(r01,returning));
    const reflected=div(add(r01,returning),denominator);
    const film=sub(reflected,r01);
    const t01=div(complex(2*cos0),add(q0,q1));
    const forward=div(t01,denominator);
    const backward=mul(forward,returning);
    const difference=((phase(film)-phase(r01))%(2*Math.PI)+2*Math.PI)%(2*Math.PI);
    return {
      wavelength, thickness, angle, sin0, cos0, sin1, cos1,
      surface:r01, film, reflected, forward, backward,
      reflectance:magnitudeSquared(reflected), phase:difference,
      opticalPath:2*OXIDE_INDEX*thickness*cos1,
      filmWavelength:wavelength/OXIDE_INDEX
    };
  };
  const field = (coefficient, wavelength, timeFs) => {
    const radians=2*Math.PI*LIGHT_SPEED*timeFs/wavelength;
    return coefficient[0]*Math.cos(radians)+coefficient[1]*Math.sin(radians);
  };
  const spatialField = (coefficient, spatialPhase) => {
    return coefficient[0]*Math.cos(spatialPhase)-coefficient[1]*Math.sin(spatialPhase);
  };
  /* Numerical CIE data obtained from colour-science/colour.
   * Copyright 2013 Colour Developers
   *
   * Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
   *
   * 1. Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
   *
   * 2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
   *
   * 3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.
   *
   * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS “AS IS” AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE
   *
   */
  const CMF = [[380,0.001368,0.000039,0.006450001],[385,0.002236,0.000064,0.01054999],[390,0.004243,0.00012,0.02005001],[395,0.00765,0.000217,0.03621],[400,0.01431,0.000396,0.06785001],[405,0.02319,0.00064,0.1102],[410,0.04351,0.00121,0.2074],[415,0.07763,0.00218,0.3713],[420,0.13438,0.004,0.6456],[425,0.21477,0.0073,1.0390501],[430,0.2839,0.0116,1.3856],[435,0.3285,0.01684,1.62296],[440,0.34828,0.023,1.74706],[445,0.34806,0.0298,1.7826],[450,0.3362,0.038,1.77211],[455,0.3187,0.048,1.7441],[460,0.2908,0.06,1.6692],[465,0.2511,0.0739,1.5281],[470,0.19536,0.09098,1.28764],[475,0.1421,0.1126,1.0419],[480,0.09564,0.13902,0.8129501],[485,0.05795001,0.1693,0.6162],[490,0.03201,0.20802,0.46518],[495,0.0147,0.2586,0.3533],[500,0.0049,0.323,0.272],[505,0.0024,0.4073,0.2123],[510,0.0093,0.503,0.1582],[515,0.0291,0.6082,0.1117],[520,0.06327,0.71,0.07824999],[525,0.1096,0.7932,0.05725001],[530,0.1655,0.862,0.04216],[535,0.2257499,0.9148501,0.02984],[540,0.2904,0.954,0.0203],[545,0.3597,0.9803,0.0134],[550,0.4334499,0.9949501,0.008749999],[555,0.5120501,1,0.005749999],[560,0.5945,0.995,0.0039],[565,0.6784,0.9786,0.002749999],[570,0.7621,0.952,0.0021],[575,0.8425,0.9154,0.0018],[580,0.9163,0.87,0.001650001],[585,0.9786,0.8163,0.0014],[590,1.0263,0.757,0.0011],[595,1.0567,0.6949,0.001],[600,1.0622,0.631,0.0008],[605,1.0456,0.5668,0.0006],[610,1.0026,0.503,0.00034],[615,0.9384,0.4412,0.00024],[620,0.8544499,0.381,0.00019],[625,0.7514,0.321,0.0001],[630,0.6424,0.265,0.00004999999],[635,0.5419,0.217,0.00003],[640,0.4479,0.175,0.00002],[645,0.3608,0.1382,0.00001],[650,0.2835,0.107,0],[655,0.2187,0.0816,0],[660,0.1649,0.061,0],[665,0.1212,0.04458,0],[670,0.0874,0.032,0],[675,0.0636,0.0232,0],[680,0.04677,0.017,0],[685,0.0329,0.01192,0],[690,0.0227,0.00821,0],[695,0.01584,0.005723,0],[700,0.01135916,0.004102,0],[705,0.008110916,0.002929,0],[710,0.005790346,0.002091,0],[715,0.004109457,0.001484,0],[720,0.002899327,0.001047,0],[725,0.00204919,0.00074,0],[730,0.001439971,0.00052,0],[735,0.0009999493,0.0003611,0],[740,0.0006900786,0.0002492,0],[745,0.0004760213,0.0001719,0],[750,0.0003323011,0.00012,0],[755,0.0002348261,0.0000848,0],[760,0.0001661505,0.00006,0],[765,0.000117413,0.0000424,0],[770,0.00008307527,0.00003,0],[775,0.00005870652,0.0000212,0],[780,0.00004150994,0.00001499,0]];
  const observer = wavelength => {
    const position=Math.max(0,Math.min(80,(wavelength-380)/5));
    const i=Math.min(79,Math.floor(position)),t=position-i;
    return [1,2,3].map(k=>CMF[i][k]*(1-t)+CMF[i+1][k]*t);
  };
  const multiplyMatrix = (matrix, vector) => matrix.map(row=>row.reduce((sum,x,i)=>sum+x*vector[i],0));
  const RGB_MATRIX=[[3.2404542,-1.5371385,-.4985314],[-.969266,1.8760108,.041556],[.0556434,-.2040259,1.0572252]];
  const BRADFORD=[[.8951,.2664,-.1614],[-.7502,1.7135,.0367],[.0389,-.0685,1.0296]];
  const INVERSE_BRADFORD=[[.9869929,-.1470543,.1599627],[.4323053,.5183603,.0492912],[-.0085287,.0400428,.9684867]];
  const wavelengths=Array.from({length:81},(_,i)=>380+5*i);
  const whiteXYZ=wavelengths.reduce((sum,w)=>observer(w).map((x,i)=>x+sum[i]),[0,0,0]);
  const sourceWhite=whiteXYZ.map(x=>x/whiteXYZ[1]);
  const sourceCone=multiplyMatrix(BRADFORD,sourceWhite);
  const targetCone=multiplyMatrix(BRADFORD,[.95047,1,1.08883]);
  const adaptWhite = xyz => multiplyMatrix(INVERSE_BRADFORD,multiplyMatrix(BRADFORD,xyz).map((x,i)=>x*targetCone[i]/sourceCone[i]));
  const linearRGB = xyz => multiplyMatrix(RGB_MATRIX,xyz).map(x=>Math.max(0,x));
  const encode = value => {
    const x=Math.max(0,Math.min(1,value));
    return Math.round(255*(x<=.0031308 ? 12.92*x : 1.055*Math.pow(x,1/2.4)-.055));
  };
  const cssRGB = rgb => {
    const ceiling=Math.max(1,...rgb);
    return 'rgb('+rgb.map(x=>encode(x/ceiling)).join(', ')+')';
  };
  const spectralRGB = wavelength => {
    const rgb=linearRGB(observer(wavelength));
    const max=Math.max(...rgb);
    return rgb.map(x=>x/max);
  };
  const wavelengthColor = wavelength => cssRGB(spectralRGB(wavelength));
  const outputColor = (thickness,angle,mode,wavelength) => {
    if (mode==='single') {
      const reflectance=sample(thickness,angle,wavelength).reflectance;
      return cssRGB(spectralRGB(wavelength).map(x=>x*reflectance));
    }
    const reflectedXYZ=wavelengths.reduce((sum,w)=>{
      const power=sample(thickness,angle,w).reflectance;
      return observer(w).map((x,i)=>sum[i]+x*power/whiteXYZ[1]);
    },[0,0,0]);
    return cssRGB(linearRGB(adaptWhite(reflectedXYZ)));
  };
  const extremum = (angle,wavelength,target,currentThickness) => {
    const choices=Array.from({length:351},(_,d)=>({d,r:sample(d,angle,wavelength).reflectance}));
    const extreme=target==='min' ? Math.min(...choices.map(x=>x.r)) : Math.max(...choices.map(x=>x.r));
    return choices.filter(x=>Math.abs(x.r-extreme)<.001).sort((a,b)=>Math.abs(a.d-currentThickness)-Math.abs(b.d-currentThickness))[0].d;
  };
  return Object.freeze({OXIDE_INDEX,LIGHT_SPEED,sample,field,spatialField,wavelengthColor,outputColor,extremum});
});

